import React, { useState } from 'react';
import StatusPill from './StatusPill';

interface StatusPillSelectProps {
  /** Pill colour key (see StatusPill). */
  pillStatus: string;
  label: string;
  /** Current raw status value the dropdown starts on. */
  value: string;
  options: Array<{ value: string; label?: string }>;
  onChange: (next: string) => Promise<void>;
  title?: string;
}

/**
 * REG-811: a status pill that is also a status dropdown. A transparent native
 * <select> sits over the pill, so it opens the platform picker (also on
 * phones) and never triggers the card click underneath.
 */
function StatusPillSelect({ pillStatus, label, value, options, onChange, title }: StatusPillSelectProps) {
  const [saving, setSaving] = useState(false);
  const hasCurrent = options.some((option) => option.value === value);
  return (
    <span
      className={`sky-status-pill-select${saving ? ' sky-status-pill-select--saving' : ''}`}
      title={title ?? 'Change status'}
      onClick={(event) => event.stopPropagation()}
    >
      <StatusPill status={pillStatus} label={`${label} ▾`} />
      <select
        aria-label={title ?? 'Change status'}
        value={value}
        disabled={saving}
        onChange={async (event) => {
          const next = event.target.value;
          if (!next || next === value) return;
          setSaving(true);
          try {
            await onChange(next);
          } finally {
            setSaving(false);
          }
        }}
      >
        {!hasCurrent && <option value={value}>{value || '—'}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label || option.value}</option>
        ))}
      </select>
    </span>
  );
}

export default React.memo(StatusPillSelect);
